import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import UserAvatar from "@/components/UserAvatar";
import { Card } from "@/components/ui/card";
import {
  Users, UserCheck, UserX, CalendarPlus, ListChecks, Cake, ShieldAlert, CalendarClock, TrendingUp,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

function KpiCard({ icon: Icon, label, value, tint, sub, delay }) {
  return (
    <Card className="p-5 border-border animate-fade-up" style={{ animationDelay: `${delay}ms` }} data-testid="kpi-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
          <p className="font-display text-3xl font-extrabold mt-2">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${tint}1a`, color: tint }}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setData(r.data));
    api.get("/dashboard/task-trend").then((r) => setTrend(r.data));
  }, []);

  if (!data) return <div className="p-8 text-muted-foreground">{t("loading")}</div>;

  const chartData = trend.map((d) => ({ name: d.date.slice(5), ...d }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("dashboard")}</h1>
        <p className="text-muted-foreground mt-1">{new Date().toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={UserCheck} label={t("workingToday")} value={data.working_today_count} tint="#3B82F6" delay={0} />
        <KpiCard icon={UserX} label={t("absent")} value={data.absent_count} tint="#EF4444" delay={60} />
        <KpiCard icon={CalendarPlus} label={t("openShifts")} value={data.open_shifts_count} tint="#F59E0B" delay={120} />
        <KpiCard icon={ListChecks} label={t("taskCompletion")} value={`${data.task_completion_rate}%`} sub={`${data.completed_tasks}/${data.total_tasks}`} tint="#10B981" delay={180} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="lg:col-span-2 p-6 animate-fade-up" style={{ animationDelay: "220ms" }}>
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h3 className="font-display font-semibold">{t("taskTrend")}</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
              <Bar dataKey="created" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} name={t("tasks")} />
              <Bar dataKey="completed" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} name={t("completed")} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Working today list */}
        <Card className="p-6 animate-fade-up" style={{ animationDelay: "260ms" }}>
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Users className="w-4 h-4 text-primary" />{t("workingToday")}</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto scrollbar-thin">
            {data.working_today.length === 0 && <p className="text-sm text-muted-foreground">{t("noData")}</p>}
            {data.working_today.map((e) => (
              <div key={e.id} className="flex items-center gap-3">
                <UserAvatar name={e.name} src={e.profile_picture} size={34} />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{e.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{e.position}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Certification alerts */}
        <Card className="p-6 animate-fade-up" style={{ animationDelay: "300ms" }}>
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-destructive" />{t("upcomingCerts")}</h3>
          <div className="space-y-3 max-h-56 overflow-y-auto scrollbar-thin">
            {data.certification_alerts.length === 0 && <p className="text-sm text-muted-foreground">{t("noData")}</p>}
            {data.certification_alerts.map((c, i) => (
              <div key={i} className="flex items-center justify-between gap-2" data-testid="cert-alert">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{c.employee_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.certification}</p>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${c.expired ? "bg-destructive/15 text-destructive" : "bg-amber-500/15 text-amber-600"}`}>
                  {c.expired ? t("expired") : c.expiry_date}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Birthdays */}
        <Card className="p-6 animate-fade-up" style={{ animationDelay: "340ms" }}>
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><Cake className="w-4 h-4 text-pink-500" />{t("birthdays")}</h3>
          <div className="space-y-3">
            {data.birthdays.length === 0 && <p className="text-sm text-muted-foreground">{t("noData")}</p>}
            {data.birthdays.map((b) => (
              <div key={b.id} className="flex items-center gap-3">
                <UserAvatar name={b.name} src={b.profile_picture} size={34} />
                <div>
                  <p className="text-sm font-medium">{b.name}</p>
                  <p className="text-xs text-muted-foreground">{b.date_of_birth?.slice(5)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Open shifts & pending */}
        <Card className="p-6 animate-fade-up" style={{ animationDelay: "380ms" }}>
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-amber-500" />{t("openShifts")} · {t("pendingTimeOff")}</h3>
          <div className="space-y-3">
            {data.open_shifts.map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{s.date}</span>
                <span className="font-medium">{s.start_time}–{s.end_time}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="text-sm text-muted-foreground">{t("pendingTimeOff")}</span>
              <span className="font-display font-bold">{data.pending_vacations}</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
